/**
 * ScanReceiptModal — phone-first receipt capture.
 *
 * Flow:
 *   1. User taps "Scan receipt" → modal opens with two CTAs:
 *        • "Take photo" (rear camera on phones via capture="environment")
 *        • "Choose file" (drag-drop / library / desktop)
 *   2. We post the image to /expenses/scan-receipt → Claude vision
 *      reads merchant / date / amount / currency / category and
 *      auto-creates an ExpenseItem on the user's draft report.
 *   3. We render a confirmation card with the extracted fields and a
 *      "Looks good — save" button. If confidence is low we surface a
 *      yellow "Please verify" pill so the user knows to double-check.
 *
 * Why a separate modal (vs inline upload): the user explicitly wants
 * to *capture* a receipt with their phone. Embedding the capture
 * step in the same flow as the multi-line manual form muddies the
 * fastest path. One tap, one photo, one confirmation.
 */
import { useRef, useState } from "react";
import {
  Camera,
  CheckCircle2,
  Image as ImageIcon,
  Loader2,
  Sparkles,
  Upload,
} from "lucide-react";

import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { useToast } from "@/hooks/useToast";

import {
  scanReceipt,
  type ScanReceiptResult,
} from "@/api/expenses";

interface Props {
  /** Optional — if the user is already inside an open report, scope
   *  the scan to that report so the line lands there. Otherwise the
   *  backend picks the most recent draft (or opens a new one). */
  reportId?: string;
  onClose: () => void;
  onScanned?: (result: ScanReceiptResult) => void;
}

function fmtMoney(amount: number | null, currency: string) {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function fmtDate(iso: string) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export function ScanReceiptModal({ reportId, onClose, onScanned }: Props) {
  const { addToast } = useToast();
  const cameraRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const [state, setState] = useState<"idle" | "scanning" | "done">("idle");
  const [preview, setPreview] = useState<string | null>(null);
  const [result, setResult] = useState<ScanReceiptResult | null>(null);

  async function handleFile(file: File) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      addToast("error", "Please choose an image file (JPG, PNG, etc.)");
      return;
    }
    setPreview(URL.createObjectURL(file));
    setState("scanning");
    try {
      const r = await scanReceipt(file, reportId);
      setResult(r);
      setState("done");
      if (r.needs_review) {
        addToast(
          "info",
          "Receipt scanned — please verify the details below before saving the report.",
        );
      } else {
        addToast(
          "success",
          `Receipt added: ${fmtMoney(r.ocr.amount, r.ocr.currency)} at ${
            r.ocr.merchant_name || "merchant"
          }`,
        );
      }
      onScanned?.(r);
    } catch (err) {
      console.error("scanReceipt failed", err);
      addToast(
        "error",
        "Could not read that receipt. Try a clearer photo, or add the line manually.",
      );
      setState("idle");
    }
  }

  function reset() {
    setResult(null);
    setPreview(null);
    setState("idle");
    if (cameraRef.current) cameraRef.current.value = "";
    if (fileRef.current) fileRef.current.value = "";
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Scan receipt"
      size="lg"
    >
      <div className="space-y-4">
        {state === "idle" && (
          <>
            <div className="flex items-start gap-3 rounded-lg border border-accent/30 bg-accent/5 p-3 text-sm">
              <Sparkles className="mt-0.5 h-5 w-5 flex-none text-accent" />
              <p className="text-text-primary">
                Snap a photo of the receipt — Lenavio AI reads the merchant,
                date, amount and category, and adds the line to your draft
                expense report automatically.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {/* Mobile camera capture. The capture="environment" hint
                  tells phones to open the rear camera by default. On
                  desktop browsers it just opens a file picker. */}
              <button
                type="button"
                onClick={() => cameraRef.current?.click()}
                className="flex flex-col items-center gap-2 rounded-lg border border-border bg-surface p-6 text-center hover:border-accent hover:bg-accent/5 transition-colors"
              >
                <div className="rounded-full bg-accent/10 p-3 text-accent">
                  <Camera className="h-6 w-6" />
                </div>
                <div className="text-sm font-semibold">Take photo</div>
                <div className="text-xs text-text-muted">
                  Opens your phone's camera
                </div>
              </button>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="flex flex-col items-center gap-2 rounded-lg border border-border bg-surface p-6 text-center hover:border-accent hover:bg-accent/5 transition-colors"
              >
                <div className="rounded-full bg-accent/10 p-3 text-accent">
                  <Upload className="h-6 w-6" />
                </div>
                <div className="text-sm font-semibold">Upload image</div>
                <div className="text-xs text-text-muted">
                  JPG / PNG from your computer
                </div>
              </button>
            </div>

            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />

            <p className="text-xs text-text-muted">
              We log every AI scan to your audit log. Average cost per
              receipt is about $0.005.
            </p>
          </>
        )}

        {state === "scanning" && (
          <Card className="flex flex-col items-center gap-3 p-8 text-center">
            {preview && (
              <img
                src={preview}
                alt="Receipt preview"
                className="max-h-48 rounded-md border border-border"
              />
            )}
            <div className="flex items-center gap-2 text-sm font-medium">
              <Loader2 className="h-5 w-5 animate-spin text-accent" />
              Reading the receipt…
            </div>
            <div className="text-xs text-text-muted">
              Extracting merchant, date, amount and category. Usually
              takes 3–6 seconds.
            </div>
          </Card>
        )}

        {state === "done" && result && (
          <ExtractionResult
            result={result}
            preview={preview}
            onRescan={reset}
            onClose={onClose}
          />
        )}
      </div>
    </Modal>
  );
}

function ExtractionResult({
  result,
  preview,
  onRescan,
  onClose,
}: {
  result: ScanReceiptResult;
  preview: string | null;
  onRescan: () => void;
  onClose: () => void;
}) {
  const { ocr, needs_review } = result;
  const confidencePct = Math.round((ocr.confidence ?? 0) * 100);
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3 rounded-lg border border-success/30 bg-success/5 p-3 text-sm">
        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-success" />
        <div>
          <div className="font-semibold text-text-primary">
            Receipt added to your draft report
          </div>
          <p className="text-text-muted">
            We extracted the fields below and pinned the photo to the new
            line. You can edit anything that looks off, then submit when
            you're ready.
          </p>
        </div>
      </div>

      <Card className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-[140px_1fr]">
        {preview && (
          <img
            src={preview}
            alt="Receipt"
            className="max-h-40 w-full rounded-md border border-border object-contain sm:max-h-none"
          />
        )}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field label="Merchant" value={ocr.merchant_name || "—"} />
          <Field label="Date" value={fmtDate(ocr.item_date)} />
          <Field
            label="Amount"
            value={fmtMoney(ocr.amount, ocr.currency)}
            tone={ocr.amount == null ? "warning" : undefined}
          />
          <Field
            label="Category"
            value={
              <span className="capitalize">{ocr.category}</span>
            }
          />
          {ocr.tax_amount != null && (
            <Field
              label="Tax"
              value={fmtMoney(ocr.tax_amount, ocr.currency)}
            />
          )}
          {ocr.tip_amount != null && (
            <Field
              label="Tip"
              value={fmtMoney(ocr.tip_amount, ocr.currency)}
            />
          )}
          <Field
            label="AI confidence"
            value={
              <Badge
                variant={
                  confidencePct >= 80
                    ? "success"
                    : confidencePct >= 50
                      ? "warning"
                      : "danger"
                }
              >
                {confidencePct}%
              </Badge>
            }
          />
        </div>
      </Card>

      {needs_review && (
        <div className="rounded-lg border border-warning/40 bg-warning/5 p-3 text-sm">
          <div className="font-semibold text-warning">Please verify</div>
          <p className="text-text-muted">
            {ocr.reason ||
              "The receipt was hard to read. Open the report and double-check the amount, date and merchant before submitting."}
          </p>
        </div>
      )}

      {ocr.line_items.length > 0 && (
        <Card className="p-4">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
            <ImageIcon className="h-3.5 w-3.5" /> Itemized
          </div>
          <ul className="space-y-1 text-sm">
            {ocr.line_items.map((li, i) => (
              <li
                key={i}
                className="flex items-center justify-between border-b border-border/40 py-1 last:border-b-0"
              >
                <span>{li.description}</span>
                <span className="tabular-nums text-text-muted">
                  {li.amount != null
                    ? fmtMoney(li.amount, ocr.currency)
                    : "—"}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" onClick={onRescan}>
          Scan another
        </Button>
        <Button onClick={onClose}>Done</Button>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  tone?: "warning";
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-text-muted">
        {label}
      </div>
      <div
        className={
          "mt-0.5 font-medium " +
          (tone === "warning" ? "text-warning" : "text-text-primary")
        }
      >
        {value}
      </div>
    </div>
  );
}

export default ScanReceiptModal;
